/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';
import { COMPARISON_RADIO_OPTIONS } from '../../constants';

/**
 * SearchComparisonPeriodSelector
 *
 * Renders a radio button group for selecting a comparison period in search searchScenarios.
 * Updates the corresponding scenario filter when a selection is made.
 *
 * @param {Object} props.filterData - Filter info for the selected scenario and visual.
 * @param {Function} props.handleClose - Callback to close the selector after selection.
 * @returns {JSX.Element} Radio group for comparison period selection
 */
const SearchComparisonPeriodSelector = ({ filterData, found, handleClose }) => {
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);
  //
  const dispatch = useDispatch();
  // Handle radio button change: update scenario filter and close selector
  const handleChange = (event, value) => {
    event.stopPropagation();
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios?.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, value),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = { ...visual.filters };
            // Update the specific filter with the new value
            Object.keys(updatedFilters).forEach((filterName) => {
              if (updatedFilters[filterName].keyword === filterData.keyword) {
                updatedFilters[filterName] = {
                  ...updatedFilters[filterName],
                  keyword: value,
                  value,
                };
              }
            });
            //
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (favoriteScenarios.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, value),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = { ...visual.filters };
            // Update the specific filter with the new value
            Object.keys(updatedFilters).forEach((filterName) => {
              if (updatedFilters[filterName].keyword === filterData.keyword) {
                updatedFilters[filterName] = {
                  ...updatedFilters[filterName],
                  keyword: value,
                  value,
                };
              }
            });
            //
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
    //
    if (searchScenarios.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, value),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = { ...visual.filters };
            // Update the specific filter with the new value
            Object.keys(updatedFilters).forEach((filterName) => {
              if (updatedFilters[filterName].keyword === filterData.keyword) {
                updatedFilters[filterName] = {
                  ...updatedFilters[filterName],
                  keyword: value,
                  value,
                };
              }
            });
            //
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    handleClose();
  };
  //
  const selectedBreakdown = COMPARISON_RADIO_OPTIONS?.find((pace) => pace === found?.value);
  //
  return (
    <Grid
      container
      flexDirection="row"
      justifyContent="flex-end"
      alignItems="center"
      wrap="nowrap"
      bgcolor="#ffffff"
      px={2}
    >
      <FormControl>
        <RadioGroup value={selectedBreakdown} onChange={handleChange}>
          {COMPARISON_RADIO_OPTIONS.map((option) => (
            <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
          ))}
        </RadioGroup>
      </FormControl>
    </Grid>
  );
};
//
export default SearchComparisonPeriodSelector;
